import * as React from "react";
import { Checkbox } from "@bizform/primereact/checkbox";
import { CheckboxProps } from "@bizform/primereact/components/checkbox/Checkbox";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { DataType } from "@bizform/type/model/model/DataType.model";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { WindowKey } from "@bizform/type/model/model/WindowKey.model";
import { ExceptionType } from "@bizform/type/model/model/Exception.model";
import { LookUp } from "@bizform/type/model/data_type/LookUp.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit, I18N } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = CheckboxProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    tabindex?: number;
    inputId?: string;
    value?: Array<LookUp>;
    name?: string;
    checked?: boolean;
    style?: object;
    className?: string;
    disabled?: boolean;
    readOnly?: boolean;
    labelStyle?: object;
    printStyle?: object;
    printClassName?: string;
    tooltipAttribute?: TooltipOptions & {text?: string, useTooltip?: boolean};
    elementWidth?: string;
    lookupDataArray?: Array<any>;
    maxCount?: number;

    // event
    onChange(e: ComponentEventType): void;
    onMouseDown?(event: Event): void;
    onContextMenu?(event: Event): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * Checkbox component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<Array<LookUp>>>()
export class BfCheckboxMultiple extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_3RFsRZ2xg1ir_2OwCyybKU0k5hbZIOI",
        category: ComponentCategory.INPUT,
        label: I18n.componentElement.checkboxMultiple.label,
        description: I18n.componentElement.checkboxMultiple.description,
        icon: "",
        dataType: [DataType.LOOKUP],
        onlyArray: true,
        pathToLimitDataTypeArray: "maxCount",
        property: null
    };

    focus(): void {
        if (this._checkbox) {
            this._checkbox.querySelector("input").focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            newState.wrappedCompState.maxCount = !newState.wrappedCompState.maxCount ? 1 : newState.wrappedCompState.maxCount;

            let isAcceptable = true;
            if (newState.wrappedCompState.value) {
                if (newState.wrappedCompState.value.length > newState.wrappedCompState.maxCount) {
                    newState.wrappedCompState.value = newState.wrappedCompState.value.slice(0, newState.wrappedCompState.maxCount);
                    isAcceptable = false;
                }
            }
            if (isAcceptable) {
                this.setState(newState);
            } else {
                this.setState(newState, this._callbackSetProperty);
            }
        } catch (e) {
            this.emitDidCatch(e);
        }
    }
    emit(value: Array<LookUp>): void {
    }

    emitFormState(value: string): void {
    }

    subscription(subscriptionValue: Array<LookUp>): void {
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                value: []
            }
        };
        let isAcceptable = true;
        if (!ComponentHelper.isNull(subscriptionValue)) {
            if (Array.isArray(subscriptionValue)) {
                if (subscriptionValue.length > 0) {
                    newState.wrappedCompState.value = subscriptionValue.map((valLookUp) => {
                        const returnValue = newState.wrappedCompState.lookupDataArray.find((item) => {
                            return valLookUp.value === item.value;
                        });
                        if (returnValue) {
                            return returnValue;
                        } else {
                            isAcceptable = false;
                            return null;
                        }
                    });
                }
            } else {
                isAcceptable = false;
            }
        }

        if (isAcceptable) {
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    lookupDataSubscription?(lookup: Array<LookUp>): void {
        if (Array.isArray(lookup)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    lookupDataArray: lookup
                }
            };

            this.setState(newState);
        }
    }
    @ReactComponentElementInit(BfCheckboxMultiple)
    static init(): ComponentElement<Array<LookUp>> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }
    /* for React */
    props: Props;
    state: State;

    private _checkbox: any;
    private _checked: {[key: string]: boolean};
    private _disabled: boolean;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: [],
                lookupDataArray: [],
                onChange: null
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
        this._setChecked = this._setChecked.bind(this);
        this._renderCheckbox = this._renderCheckbox.bind(this);
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate(): void {
        if (this._checkbox) {
            this._makeStyle();
            if (this.state.wrappedCompState.tabindex !== this.___tabindex) {
                ComponentHelper.tabindex(this.displayMode, Array.from(this._checkbox.querySelectorAll("input")), this.state.wrappedCompState.tabindex);
                this.___tabindex = this.state.wrappedCompState.tabindex;
            }
        }
    }

    componentWillUnmount(): void {
        this._checkbox = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    private _onChangeEvent(e: ComponentEventType): void {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: e.checked
                }
            };
            if (e.checked) {
                const newValue = [...newState.wrappedCompState.value, e.value];
                if (newState.wrappedCompState.maxCount >= newValue.length) {
                    newState.wrappedCompState.value = newValue;
                    this.setState(newState, this._callbackSetState);
                } else {
                    window[WindowKey.CORELIBRARY].ExceptionHandler.toast(ExceptionType.INFO, I18N.translate("{$I._app.accessibility.component.multipleCheckbox.max}"));
                }
            } else {
                const removeIndex = newState.wrappedCompState.value.indexOf(e.value);
                const newValue = [...newState.wrappedCompState.value];
                newValue.splice(removeIndex, 1);
                newState.wrappedCompState.value = newValue;
                this.setState(newState, this._callbackSetState);
            }
        }
    }

    private _callbackSetProperty(): void {
        this.emit(this.state.wrappedCompState.value);
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.value);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            this._disabled = false;
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: Props): React.ReactNode {
        if (this.state.wrappedCompState.tooltipAttribute && this.state.wrappedCompState.tooltipAttribute.useTooltip) {
            delete(newProps.tooltipOptions);
        }
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        this._checked = {};
        if (Array.isArray(this.state.wrappedCompState.value)) {
            this.state.wrappedCompState.value.forEach(this._setChecked);
        }
        return (
            <div ref={this._setRef} tabIndex={0} style={{outlineColor: "#8dcdff"}}>
                {this.state.wrappedCompState.lookupDataArray.map(this._renderCheckbox)}
                <div className="clearfix"></div>
            </div>
        );
    }

    private _renderCheckbox(obj: LookUp, index: number): React.ReactNode {
        let key = this.elementId + "_" + index;
        if (this.state.wrappedCompState.tooltipAttribute && this.state.wrappedCompState.tooltipAttribute.useTooltip) {
            key = ComponentHelper.tooltipValue(this.state.wrappedCompState, obj.label, this.elementId, this.accessibility, index);
        }
        if (this.state.wrappedCompState.elementWidth) {
            return (
                <div key={index} className="m-1" style={{display: "inline-block", float: "left", width: this.state.wrappedCompState.elementWidth }}>
                    <Checkbox
                        {...this.state.wrappedCompState}
                        key={key}
                        name={this.elementId}
                        inputId={`${this.elementId}_${index}`}
                        checked={this._checked[obj.value]}
                        style={null}
                        value={obj}
                        disabled={this._disabled}
                    />
                    <label htmlFor={`${this.elementId}_${index}`} className="p-checkbox-label" style={{...this.state.wrappedCompState.labelStyle, cursor: "pointer"}}>{obj.label}</label>
                </div>
            );
        } else {
            return (
                <div key={`${this.elementId}_${index}`} className="m-1" style={{display: "inline-block"}}>
                    <Checkbox
                        {...this.state.wrappedCompState}
                        key={key}
                        name={this.elementId}
                        inputId={`${this.elementId}_${index}`}
                        checked={this._checked[obj.value]}
                        style={null}
                        value={obj}
                        disabled={this._disabled}
                    />
                    <label htmlFor={`${this.elementId}_${index}`} className="p-checkbox-label" style={{...this.state.wrappedCompState.labelStyle, cursor: "pointer"}}>{obj.label}</label>
                </div>
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            lookupDataArray: null
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            lookupDataArray: null,
            readOnly: true
        };

        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            lookupDataArray: null,
            disabled: true
        };
        this._disabled = true;
        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        let value = [];
        if (this.state.wrappedCompState.value) {
            value = this.state.wrappedCompState.value.map((x, i) => {
                return x.label;
            });
        }
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {wordBreak: "break-all", ...this.state.wrappedCompState.printStyle} : null} className= {`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{value.join(",")}</div>
        );
    }

    private _setRef(ref): void {
        if (ref) {
            this._checkbox = ref;
            this._makeStyle();
            this.readyComponent();
        }
    }

    private _setChecked(item: LookUp) {
        this._checked[item.value] = true;
    }

    private _makeStyle(newProps = {...this.state.wrappedCompState}) {
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT || this.state.displayMode === ComponentElementDisplayMode.READONLY || this.state.displayMode === ComponentElementDisplayMode.DISABLED) {
            if (this._checkbox) {
                let div = this._checkbox.querySelectorAll("div");
                if (div && div.length > 0) {
                    for (let i = 3; i < div.length; i = i + 4) {
                        div[i].setAttribute("style", "");
                        for (let item in newProps.style) {
                            (div as any)[i].style[item] = (newProps as any).style[item];
                        }
                    }
                }
            }
        }
    }
}
